import { Auth } from 'aws-amplify';
import { useEffect } from 'react';
import { clearState } from 'localStorage';
import { ReduxCommandCreator } from 'appRedux/actions';
import { useDispatch, useSelector } from 'react-redux';
import { IState } from 'appRedux/createStore';
import { clearInsightSummaryCache } from 'services/insightCasino';
import { clearAllInsightSummaryPersistentCache } from 'lib/insightSummaryPersistentCache';
import { clearApplicationConfigurationsAll } from 'lib/applicationConfig';
import { clearLoginCasinoReveal, clearRandomOutcomesSession } from 'lib/loginCasinoReveal';
import { clearLinkPlayerDue } from 'lib/linkPlayerFlow';

// Maximum time to wait for Auth.signOut() before forcing a redirect anyway.
const LOGOUT_TIMEOUT_MS = 5000;

const LogoutPage = () => {
  const dispatch = useDispatch();
  const commands = ReduxCommandCreator(dispatch);
  const isCasinoAccount = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.isCasinoAccount
  ) as boolean;

  useEffect(() => {
    let isIgnore = false;

    // Use window.location.replace so /logout is never left in browser history
    // (prevents Chrome/Safari tab-restore or reload from landing back here).
    const doRedirect = () => {
      // Drop in-memory + IndexedDB insight caches and persisted casino list so a
      // non-member on this device never sees a cached unlocked Coming Soon list.
      clearInsightSummaryCache();
      void clearAllInsightSummaryPersistentCache();
      clearApplicationConfigurationsAll();
      // End the Random Outcomes session (it shows again on the next login) and drop
      // any pending casino-entry notes.
      clearRandomOutcomesSession();
      clearLoginCasinoReveal();
      clearLinkPlayerDue();
      commands.ClearStore();
      clearState();
      window.location.replace(isCasinoAccount ? '/auth/casinoadmin' : '/');
    };

    // Fallback: if Auth.signOut() hangs, redirect anyway after timeout.
    const timeoutId = setTimeout(() => {
      if (!isIgnore) doRedirect();
    }, LOGOUT_TIMEOUT_MS);

    Auth.signOut()
      .then(() => {
        if (!isIgnore) {
          clearTimeout(timeoutId);
          doRedirect();
        }
      })
      .catch((error) => {
        // signOut can throw when the user is already signed out (e.g. on a
        // stale /logout reload). Always redirect so the user is never stuck
        // on the blank screen.
        console.error('Error signing out: ', error);
        if (!isIgnore) {
          clearTimeout(timeoutId);
          doRedirect();
        }
      });

    return () => {
      isIgnore = true;
      clearTimeout(timeoutId);
    };
  }, []);

  return <></>;
};

export default LogoutPage;
export { LogoutPage };
